import React, { useMemo } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import {
  headerCellStyle,
  cellStyle,
} from "../../components/tableHelpers/TableStyleUtils";
import "./TopsTables.css";

export default function TopsByDestinationTable({ model, top }) {
  const { t } = useTranslation();

  //============================================================================================================
  // Compute table data
  //============================================================================================================
  const tableData = useMemo(() => {
    const destinations = Array.from(model.quotesByDestination.keys()); // Get all unique destinations from the quotes

    // stats by carrier
    const stats = new Map();
    (model.normCarriers || []).forEach((c) => {
      //Iterates through normalized carriers
      stats.set(c.id, {
        name: c.name,
        perDestination: Object.fromEntries(destinations.map((d) => [d, "0%"])), // Initializes percentage for each destination
        total: 0, // Total quotes for this carrier
      });
    });

    // For each destination, use the reference (top1 or topN) already calculated
    destinations.forEach((dest) => {
      const destQuotes = model.quotesByDestination.get(dest) || [];
      const ref = model.topByDestination.get(dest) || {
        top1: null,
        topN: null,
      };
      const refValue = top === 1 ? ref.top1 : ref.topN;

      // Group quotes by carrier for this destination
      const byCarrier = new Map();
      destQuotes.forEach((q) => {
        // Iterates through quotes for the destination
        const cId = q.CarrierId ?? q.Carrier?.Id; // Gets the carrier ID, handling both direct and nested structures
        if (cId == null) return; // Skip if no carrier ID
        const arr = byCarrier.get(cId) || []; // Gets existing array or creates a new one
        arr.push(q); // Adds the quote to the array
        byCarrier.set(cId, arr); // Updates the map with the new array
      });

      byCarrier.forEach((arr, cId) => {
        // Iterates through each carrier's quotes for this destination
        if (!stats.has(cId)) {
          // If the carrier is not already in stats, initialize it
          stats.set(cId, {
            name: arr[0]?.CarrierName ?? String(cId), // Uses the first quote's carrier name or the ID as a fallback
            perDestination: Object.fromEntries(
              destinations.map((d) => [d, "0%"])
            ), // Initializes percentages for all destinations
            total: 0, // Total quotes for this carrier
          });
        }
        const matching =
          refValue != null
            ? arr.filter((q) => q.BidValue <= refValue).length
            : 0; // Counts how many quotes match the reference value
        const pct = arr.length ? Math.round((matching / arr.length) * 100) : 0; // Calculates the percentage of matching quotes

        const s = stats.get(cId); // Gets the stats for this carrier
        s.perDestination[dest] = `${pct}%`; // Updates the percentage for this destination
        s.total += matching; // Increments the total count of matching quotes for this carrier
      });
    });

    // Returns the final table data structure
    return {
      destinations,
      rows: (model.normCarriers || []).map((c) => {
        const s = stats.get(c.id) ?? {
          name: c.name,
          perDestination: Object.fromEntries(
            destinations.map((d) => [d, "0%"])
          ),
          total: 0,
        };
        return { name: s.name, values: s.perDestination, total: s.total };
      }),
    };
  }, [model, top]);

  //============================================================================================================
  // Render the table
  //============================================================================================================
  return (
    // Wrapper + inner scroll container to allow horizontal scrolling only on the table
    <div className="tops-table-wrapper" style={{ marginTop: "40px" }}>
      <span
        style={{
          fontWeight: "bold",
          color: "var(--color-red-dark)",
          fontSize: "16px",
          display: "inline-block",
          marginBottom: "10px",
        }}
      >
        {t("TopDestination", { top })}
      </span>

      <div className="tops-table-container">
        <table className="tops-table tops-table-destination">
          <thead>
            <tr>
              <th style={headerCellStyle()} className="tops-col-carrier">
                {t("Carrier")}
              </th>
              {tableData.destinations.map((dest) => (
                <th
                  key={dest}
                  style={headerCellStyle()}
                  className="tops-col-main"
                >
                  {t(dest)}
                </th>
              ))}
              <th style={headerCellStyle()} className="tops-col-total">
                {t("Total")}
              </th>
            </tr>
          </thead>
          <tbody>
            {tableData.rows.map((row, idx) => (
              <tr key={idx}>
                <td
                  style={cellStyle({ background: "var(--color-white)" })}
                  className="tops-col-carrier tops-cell"
                >
                  {row.name}
                </td>
                {tableData.destinations.map((dest) => (
                  <td
                    key={dest}
                    style={cellStyle({ background: "var(--color-white)" })}
                    className="tops-col-main tops-cell"
                  >
                    {row.values[dest] || "0%"}
                  </td>
                ))}
                <td
                  style={cellStyle({ background: "var(--color-white)" })}
                  className="tops-col-total tops-cell"
                >
                  {row.total}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

TopsByDestinationTable.propTypes = {
  model: PropTypes.shape({
    normCarriers: PropTypes.array.isRequired,
    quotesByDestination: PropTypes.instanceOf(Map).isRequired,
    topByDestination: PropTypes.instanceOf(Map).isRequired,
  }).isRequired,
  top: PropTypes.oneOf([1, 2]).isRequired,
};
